import Queue from 'async-await-queue'
import amapi from '@/api/amapi'
import {
  FANOUT_CONCURRENCY,
  MAILBOX_SERVICE_STATUSES,
  MAILBOX_SERVICE_TYPE,
  MAILBOX_STATUS_ACTIVE,
  MAILBOX_STATUS_ON_HOLD,
  MAILBOX_STATUS_SUSPENDED,
  RESTRICTION_ALLOW,
  RESTRICTION_HOLD,
  RESTRICTION_SUSPEND,
  UNLIMITED_QUOTA,
  domainInScope,
} from '@/consts/email'

/**
 * Account-wide mailbox data for the Email dashboard.
 *
 * This is deliberately separate from `email.js`, which is the per-service module
 * bound to a single open hosting account and used by the cPanel Email pages.
 * Nothing here touches that module.
 *
 * There is no account-wide back-end endpoint yet (see docs/amapi-email.md § 3),
 * so the list is assembled in the browser by calling cPanel once per service.
 *
 * IMPORTANT: `amapi.cpanel_uapi` falls back to `store.getters.hosting.id` when
 * no `id` is passed. In a cross-service view that getter points at whichever
 * service was last opened, or at nothing — so every call below passes an
 * explicit `id` and `_username`.
 */

const initialState = () => ({
  // Flat list across every service, newest load replacing that service's rows.
  mailboxes: [],
  // serviceId -> total forwarder count, kept so the Aliases stat card can be
  // summed without walking the mailbox list.
  forwardersByService: {},
  loadedServiceIds: [],
  // Services that failed to load, surfaced as a non-blocking notice.
  errors: [],
  status: 'idle', // idle | loading
  // Every domain that could hold a mailbox, across every service. Needed to
  // create a mailbox on a domain that has none yet, which is why it cannot be
  // derived from `mailboxes`.
  domains: [],
  domainsStatus: 'idle', // idle | loading | loaded
  // Services whose domain list could not be read. Kept apart from `errors`,
  // which the account list renders as "Unavailable" mailbox counts — a domain
  // failure only costs the nested addon rows and the create form's options.
  domainErrors: [],
  // serviceId -> the account's addon domain names, shown under its main domain
  // on the Email pages. Kept apart from `domains`, which also holds the main
  // domain and subdomains because the create form offers all of them.
  addonDomainsByService: {},
})

const state = initialState()

/**
 * The services whose mailboxes this dashboard covers.
 *
 * Shared by the mailbox fan-out and the domain fan-out so the two can never
 * disagree about which services are in play.
 */
const mailboxServices = (rootGetters) => {
  return (rootGetters.services || []).filter(service => {
    return service.type === MAILBOX_SERVICE_TYPE &&
      MAILBOX_SERVICE_STATUSES.includes(service.status)
  })
}

/**
 * Alias counts keyed by the address the mail ends up in.
 *
 * `Email::list_forwarders` names its fields the opposite way round to what you
 * would guess: `dest` is the forwarder's own address and `forward` is where the
 * mail is delivered. Confirmed against EmailForwardersTable.vue, whose columns
 * read "Email address" -> `dest` and "Forward to" -> `forward`, and whose delete
 * call maps `dest` -> `address`, `forward` -> `forwarder`.
 */
const aliasCountsByTarget = (forwarders) => {
  return forwarders.reduce((carry, row) => {
    const target = String(row?.forward || '').toLowerCase()
    if (!target) return carry
    carry[target] = (carry[target] || 0) + 1
    return carry
  }, {})
}

/**
 * A mailbox's restriction settings, as the Security tab edits them.
 *
 * cPanel restricts each part of a mailbox independently: `suspended_login`
 * stops the customer signing in, `suspended_incoming` stops mail arriving,
 * `suspended_outgoing` rejects mail sent from it and `hold_outgoing` queues that
 * mail instead of sending it. The login and incoming flags are confirmed present
 * on every `list_pops_with_disk` row against live data (2026-09-23), as numbers
 * rather than the strings some cPanel builds return; `Number()` copes with both.
 *
 * A row that carries none of the fields reads as unrestricted. That is the safe
 * default: wrongly calling a working mailbox suspended is alarming, while the
 * reverse is merely uninformative.
 */
const mailboxRestrictions = (row) => {
  const flag = (value) => Number(value) === 1

  let outgoing = RESTRICTION_ALLOW
  if (flag(row.suspended_outgoing)) outgoing = RESTRICTION_SUSPEND
  else if (flag(row.hold_outgoing)) outgoing = RESTRICTION_HOLD

  return {
    incoming: flag(row.suspended_incoming) ? RESTRICTION_SUSPEND : RESTRICTION_ALLOW,
    outgoing,
    login: flag(row.suspended_login) ? RESTRICTION_SUSPEND : RESTRICTION_ALLOW,
  }
}

/**
 * A mailbox's own status.
 *
 * Any suspension on its own means the mailbox is not working normally, so they
 * all fold into a single "Suspended". Held outgoing mail is its own status (AM-1104,
 * 2026-10-01): mail is delayed rather than refused, so it is a warning, not a
 * failure. A suspension outranks a hold.
 *
 * This deliberately replaces the service's status. A service is only fanned out
 * at all when it is Active or Pending (MAILBOX_SERVICE_STATUSES), so nothing is
 * lost, and the PM asked for the mailbox's status rather than its plan's.
 */
const mailboxStatus = (restrictions) => {
  const values = Object.values(restrictions)
  if (values.includes(RESTRICTION_SUSPEND)) return MAILBOX_STATUS_SUSPENDED
  if (values.includes(RESTRICTION_HOLD)) return MAILBOX_STATUS_ON_HOLD
  return MAILBOX_STATUS_ACTIVE
}

// cPanel functions that put each restriction in place, and that lift it again.
const RESTRICTION_CALLS = {
  incoming: { [RESTRICTION_SUSPEND]: ['suspend_incoming', 'unsuspend_incoming'] },
  outgoing: {
    [RESTRICTION_SUSPEND]: ['suspend_outgoing', 'unsuspend_outgoing'],
    [RESTRICTION_HOLD]: ['hold_outgoing', 'release_outgoing'],
  },
  login: { [RESTRICTION_SUSPEND]: ['suspend_login', 'unsuspend_login'] },
}

const planLabel = (service) => {
  // Dedicated Email Hosting products do not exist in WHMCS yet, so in practice
  // every mailbox today takes the "Included with" branch. The other branch is
  // here for when the product group is added (docs/amapi-email.md § 5).
  return service.groupname === 'Email Hosting'
    ? service.name
    : `Included with ${service.name}`
}

const buildMailbox = (row, service, aliasCounts) => {
  const email = row.email || `${row.user}@${row.domain}`
  const restrictions = mailboxRestrictions(row)

  return {
    // An address is only unique within a service, so the row key needs both.
    id: `${service.id}:${email}`,
    email,
    user: row.user,
    domain: row.domain,
    login: row.login || email,
    // `_diskused` is in bytes.
    diskUsed: Number(row._diskused) || 0,
    // `_diskquota` is in bytes too, and is what the Storage card subtracts to
    // get the remaining space. An unlimited mailbox reports 0 or nothing here,
    // and takes the `unlimited` branch instead, which never reads it.
    diskQuota: Number(row._diskquota) || 0,
    // cPanel reports an empty mailbox's usage as 'None', which reads as a missing
    // quota. Not the same 'None' as UNLIMITED_QUOTA, which is on humandiskquota.
    humandiskused: row.humandiskused === 'None' ? '0 B' : row.humandiskused,
    humandiskquota: row.humandiskquota,
    diskusedpercent_float: row.diskusedpercent_float,
    unlimited: row.humandiskquota === UNLIMITED_QUOTA,
    aliases: aliasCounts[email.toLowerCase()] || 0,
    serviceId: service.id,
    // The account's main domain is what the Email routes address it by, so
    // every link to this mailbox needs it — see `accountPath()`.
    mainDomain: service.domain,
    // Needed as `_username` on every follow-up cPanel call for this mailbox.
    serviceUsername: service.username,
    serviceName: service.name,
    planLabel: planLabel(service),
    restrictions,
    status: mailboxStatus(restrictions),
  }
}

const getters = {
  mailboxes: state => state.mailboxes,
  mailboxesStatus: state => state.status,
  mailboxErrors: state => state.errors,

  /**
   * One mailbox, by service and address.
   *
   * Compared as strings because `service.id` may well be a number while the
   * address arrives from the URL, and the address ignores case for the same
   * reason.
   */
  mailboxFor: state => (serviceId, email) => {
    const address = String(email || '').toLowerCase()
    return state.mailboxes.find(mailbox => {
      return String(mailbox.serviceId) === String(serviceId) &&
        mailbox.email.toLowerCase() === address
    }) || null
  },

  mailboxDomainsStatus: state => state.domainsStatus,

  /**
   * Domains as `v-select` items, grouped under a heading per service.
   *
   * A domain is only unique within a service, so the value carries the service
   * id too — the same `serviceId:thing` key shape `buildMailbox` uses. Headings
   * are added only when more than one service has domains, since with a single
   * service they would be noise.
   */
  mailboxDomainOptions: state => {
    const byService = state.domains.reduce((carry, entry) => {
      const group = carry.find(item => item.serviceId === entry.serviceId)
      if (group) {
        group.domains.push(entry)
      } else {
        carry.push({ serviceId: entry.serviceId, serviceName: entry.serviceName, domains: [entry] })
      }
      return carry
    }, [])

    return byService.reduce((carry, group) => {
      if (byService.length > 1) {
        carry.push({ header: group.serviceName })
      }
      group.domains.forEach(entry => {
        carry.push({ text: entry.domain, value: `${entry.serviceId}:${entry.domain}` })
      })
      return carry
    }, [])
  },

  mailboxDomainFor: state => (key) => {
    return state.domains.find(entry => `${entry.serviceId}:${entry.domain}` === key) || null
  },

  /**
   * One service's domain names, as plain `v-select` items.
   *
   * Separate from `mailboxDomainOptions`, which spans every service and so has
   * to carry the service id in its value. An alias lives on the same cPanel
   * account as the mailbox it delivers to, so there the service is already
   * known and a bare domain name is unambiguous.
   */
  mailboxDomainsForService: state => (serviceId) => {
    return state.domains
      .filter(entry => String(entry.serviceId) === String(serviceId))
      .map(entry => entry.domain)
      .sort()
  },

  mailboxDomainErrors: state => state.domainErrors,

  /**
   * One row per hosting account, as the Email pages list them.
   *
   * The account is the unit the customer manages mail by, and its main domain
   * is how the routes address it. Counts are derived from the loaded mailboxes,
   * so they read 0 until `loadMailboxes` has reached that service.
   */
  emailAccounts: (state, getters, rootState, rootGetters) => {
    return mailboxServices(rootGetters)
      .map(service => {
        const addonDomains = state.addonDomainsByService[service.id] || []
        const stats = (scope) => {
          const mailboxes = getters.scopedMailboxes(service.id, scope)
          return {
            mailboxCount: mailboxes.length,
            storageUsed: mailboxes.reduce((sum, mailbox) => sum + mailbox.diskUsed, 0),
          }
        }
        // Counts are per listed domain: the main row leaves out what its
        // addon rows already show.
        return {
          serviceId: service.id,
          serviceUsername: service.username,
          mainDomain: service.domain,
          planLabel: planLabel(service),
          addonDomains,
          addons: addonDomains.map(domain => ({ domain, ...stats(domain) })),
          ...stats(service.domain),
          aliases: state.forwardersByService[service.id] || 0,
        }
      })
      .sort((a, b) => String(a.mainDomain).localeCompare(String(b.mainDomain)))
  },

  /**
   * One account by its main domain. Compared case-insensitively because the
   * domain arrives from the URL, where the customer may have typed it.
   */
  emailAccountFor: (state, getters) => (mainDomain) => {
    const domain = String(mainDomain || '').toLowerCase()
    return getters.emailAccounts.find(account => String(account.mainDomain).toLowerCase() === domain) || null
  },

  /**
   * One service's mailboxes, narrowed to the domain opened as an account: the
   * main domain or one of its addon domains — see domainInScope.
   */
  scopedMailboxes: state => (serviceId, scope) => {
    const addons = state.addonDomainsByService[serviceId] || []
    return state.mailboxes.filter(mailbox => {
      return String(mailbox.serviceId) === String(serviceId) &&
        domainInScope(mailbox.domain, scope, addons)
    })
  },

  /**
   * One service's domain names that belong to the domain opened as an
   * account — the same split as scopedMailboxes, applied to the domains a new
   * forwarder or mailbox can go on.
   */
  scopedDomains: (state, getters) => (serviceId, scope) => {
    const addons = state.addonDomainsByService[serviceId] || []
    return getters.mailboxDomainsForService(serviceId)
      .filter(domain => domainInScope(domain, scope, addons))
  },
}

const mutations = {
  SET_STATUS (state, status) {
    state.status = status
  },
  // Replaces just this service's rows, so a single service can be re-loaded
  // after a mutation without refetching the whole account.
  SET_SERVICE_MAILBOXES (state, { serviceId, mailboxes, aliases }) {
    state.mailboxes = state.mailboxes
      .filter(mailbox => mailbox.serviceId !== serviceId)
      .concat(mailboxes)
    state.forwardersByService = { ...state.forwardersByService, [serviceId]: aliases }
    if (!state.loadedServiceIds.includes(serviceId)) {
      state.loadedServiceIds.push(serviceId)
    }
  },
  SET_SERVICE_ERROR (state, error) {
    state.errors = state.errors
      .filter(item => item.serviceId !== error.serviceId)
      .concat(error)
  },
  CLEAR_SERVICE_ERROR (state, serviceId) {
    state.errors = state.errors.filter(item => item.serviceId !== serviceId)
  },
  SET_DOMAINS_STATUS (state, status) {
    state.domainsStatus = status
  },
  // Replaces just this service's domains, matching SET_SERVICE_MAILBOXES.
  SET_SERVICE_DOMAINS (state, { serviceId, domains }) {
    state.domains = state.domains
      .filter(entry => entry.serviceId !== serviceId)
      .concat(domains)
  },
  // Reassigned rather than keyed in place: Vue 2 cannot observe a new property
  // added to an existing object.
  SET_ADDON_DOMAINS (state, { serviceId, domains }) {
    state.addonDomainsByService = { ...state.addonDomainsByService, [serviceId]: domains }
  },
  SET_DOMAIN_ERROR (state, error) {
    state.domainErrors = state.domainErrors
      .filter(item => item.serviceId !== error.serviceId)
      .concat(error)
  },
  CLEAR_DOMAIN_ERROR (state, serviceId) {
    state.domainErrors = state.domainErrors.filter(item => item.serviceId !== serviceId)
  },
  CLEAR_MAILBOXES (state) {
    Object.assign(state, initialState())
  },
}

const actions = {
  /**
   * Load every mailbox the customer owns, one service at a time.
   *
   * Each service commits as it lands, so the table fills progressively instead
   * of waiting on the slowest server.
   */
  async loadMailboxes ({ commit, dispatch, rootGetters, state }, { force = false } = {}) {
    if (state.status === 'loading') return
    if (state.loadedServiceIds.length && !force) return

    commit('SET_STATUS', 'loading')

    try {
      if (!rootGetters.services || !rootGetters.services.length) {
        await dispatch('getServices', {}, { root: true })
      }

      const services = mailboxServices(rootGetters)

      const queue = new Queue(FANOUT_CONCURRENCY, 100)
      for (const service of services) {
        // eslint-disable-next-line symbol-description
        const task = Symbol()
        await queue.wait(task, -1)
        dispatch('loadServiceMailboxes', service)
          .catch(e => console.warn(e))
          .finally(() => queue.end(task))
      }
      await queue.flush()
    } finally {
      commit('SET_STATUS', 'idle')
    }
  },

  /**
   * Load one service's mailboxes and alias counts.
   *
   * Failures are isolated: a service that cannot be reached is recorded and the
   * rest of the dashboard still renders.
   */
  async loadServiceMailboxes ({ commit }, service) {
    const base = {
      id: service.id,
      _username: service.username,
      uapi_module: 'Email',
    }

    // Aliases are a nice-to-have. If only the forwarder call fails we still show
    // the mailboxes, with a zero alias count for this service.
    let aliasCounts = {}
    let aliasTotal = 0
    try {
      const response = await amapi.cpanel_uapi({ ...base, uapi_function: 'list_forwarders' })
      const forwarders = response?.data || []
      aliasCounts = aliasCountsByTarget(forwarders)
      aliasTotal = forwarders.length
    } catch (e) {
      console.warn(`emailHub: unable to list forwarders for service ${service.id}`, e)
    }

    try {
      const response = await amapi.cpanel_uapi({
        ...base,
        uapi_function: 'list_pops_with_disk',
        additional: { skip_main: 1 },
      })
      const mailboxes = (response?.data || []).map(row => buildMailbox(row, service, aliasCounts))

      commit('CLEAR_SERVICE_ERROR', service.id)
      commit('SET_SERVICE_MAILBOXES', {
        serviceId: service.id,
        mailboxes,
        aliases: aliasTotal,
      })
    } catch (e) {
      console.warn(`emailHub: unable to load mailboxes for service ${service.id}`, e)
      commit('SET_SERVICE_ERROR', {
        serviceId: service.id,
        domain: service.domain,
      })
      throw e
    }
  },

  /**
   * Re-read a single service after a mailbox was changed or deleted.
   */
  async refreshService ({ dispatch }, serviceId) {
    const service = await dispatch('resolveService', serviceId)
    if (!service) return

    try {
      await dispatch('loadServiceMailboxes', service)
    } catch (e) {
      console.warn(e)
    }
  },

  /**
   * Load every domain that could hold a mailbox, one service at a time.
   *
   * Deliberately not derived from `mailboxes`: creating the first mailbox on a
   * domain means offering domains that have no mailbox yet, so this has to be
   * its own fan-out. Only called when the create form opens.
   */
  async loadDomains ({ commit, dispatch, rootGetters, state }, { force = false } = {}) {
    if (state.domainsStatus === 'loading') return
    if (state.domainsStatus === 'loaded' && !force) return

    commit('SET_DOMAINS_STATUS', 'loading')

    try {
      if (!rootGetters.services || !rootGetters.services.length) {
        await dispatch('getServices', {}, { root: true })
      }

      const queue = new Queue(FANOUT_CONCURRENCY, 100)
      for (const service of mailboxServices(rootGetters)) {
        // eslint-disable-next-line symbol-description
        const task = Symbol()
        await queue.wait(task, -1)
        dispatch('loadServiceDomains', service)
          .catch(e => console.warn(e))
          .finally(() => queue.end(task))
      }
      await queue.flush()
      commit('SET_DOMAINS_STATUS', 'loaded')
    } catch (e) {
      commit('SET_DOMAINS_STATUS', 'idle')
      throw e
    }
  },

  /**
   * One service's mailbox-capable domains.
   *
   * `DomainInfo::list_domains` answers with `main_domain`, `sub_domains`,
   * `addon_domains` and `parked_domains`. Parked domains are left out, matching
   * DomainSelect.parseData() — they are aliases of another domain and cPanel
   * does not hold mail for them in their own right.
   *
   * Failures are recorded rather than swallowed. Without this a service whose
   * domains could not be read is indistinguishable from one that has none, and
   * the create form tells the customer they have no domains when the call in
   * fact failed.
   */
  async loadServiceDomains ({ commit }, service) {
    try {
      const response = await amapi.cpanel_uapi({
        id: service.id,
        _username: service.username,
        uapi_module: 'DomainInfo',
        uapi_function: 'list_domains',
      })

      const data = response?.data || {}
      const names = [
        data.main_domain,
        ...(data.sub_domains || []),
        ...(data.addon_domains || []),
      ].filter(Boolean)

      commit('CLEAR_DOMAIN_ERROR', service.id)
      commit('SET_ADDON_DOMAINS', {
        serviceId: service.id,
        domains: [...(data.addon_domains || [])].filter(Boolean).sort(),
      })
      commit('SET_SERVICE_DOMAINS', {
        serviceId: service.id,
        domains: names.map(domain => ({
          domain,
          serviceId: service.id,
          serviceUsername: service.username,
          serviceName: service.name,
        })),
      })
    } catch (e) {
      console.warn(`emailHub: unable to list domains for service ${service.id}`, e)
      commit('SET_DOMAIN_ERROR', {
        serviceId: service.id,
        domain: service.domain,
      })
      throw e
    }
  },

  /**
   * One service's domains, addressed by id.
   *
   * `loadDomains` fans out over every service, which is what the create form
   * needs and far more than a single mailbox's alias form does. This resolves
   * one service and reuses the same per-service load, so both paths share
   * `SET_SERVICE_DOMAINS` and its error handling. Already-loaded domains cost
   * no request; a service whose last attempt failed is retried.
   */
  async loadDomainsForService ({ dispatch, getters }, serviceId) {
    const service = await dispatch('resolveService', serviceId)
    if (!service) return

    if (getters.mailboxDomainsForService(service.id).length) return

    await dispatch('loadServiceDomains', service)
  },

  /**
   * Create a mailbox on a given service.
   *
   * `add_pop` takes the local part as `email` and the domain separately, not a
   * full address. Quota is in MB. The service is re-read afterwards so the table
   * and the stat cards pick the new mailbox up.
   */
  async createMailbox ({ dispatch }, { serviceId, email, domain, password, quota }) {
    const service = await dispatch('resolveService', serviceId)
    if (!service) throw new Error('UNK_SRV')

    await amapi.cpanel_uapi({
      id: service.id,
      _username: service.username,
      uapi_module: 'Email',
      uapi_function: 'add_pop',
      email,
      domain,
      password,
      quota,
    })

    await dispatch('loadServiceMailboxes', service)
  },

  /**
   * Resolve a service by id, tolerating the string ids that arrive from a route.
   *
   * `getHosting` filters with `===` (store/modules/hosting/actions.js:27), so a
   * route param compared against a numeric service id would never match and
   * would throw UNK_SRV. Resolving here once means everything downstream passes
   * the service's own `id`, whatever type it happens to be, and nothing else has
   * to care.
   */
  async resolveService ({ dispatch, rootGetters }, serviceId) {
    if (!rootGetters.services || !rootGetters.services.length) {
      await dispatch('getServices', {}, { root: true })
    }
    return (rootGetters.services || [])
      .find(service => String(service.id) === String(serviceId)) || null
  },

  /**
   * Resolve a service by the domain the Email routes address an account by:
   * its main domain, or one of its addon domains, which is listed as an account
   * of its own (AM-1104, 2026-10-07).
   *
   * The main domain is matched against `service.domain`, among the same
   * services the dashboard covers, with no cPanel call. Only an unmatched
   * domain costs the domain fan-out. Comparisons ignore case because the
   * domain comes from the URL.
   */
  async resolveServiceByDomain ({ dispatch, rootGetters, state }, mainDomain) {
    if (!rootGetters.services || !rootGetters.services.length) {
      await dispatch('getServices', {}, { root: true })
    }
    const domain = String(mainDomain || '').toLowerCase()
    const services = mailboxServices(rootGetters)
    const byMain = services.find(service => String(service.domain || '').toLowerCase() === domain)
    if (byMain) return byMain

    // An addon domain opened as its own account. Its owner is only known once
    // the domain lists are read; a failure there leaves it unresolved.
    try {
      await dispatch('loadDomains')
    } catch (e) {
      console.warn('emailHub: unable to list domains while resolving an addon domain', e)
    }
    return services.find(service => {
      return (state.addonDomainsByService[service.id] || [])
        .some(addon => String(addon).toLowerCase() === domain)
    }) || null
  },

  /**
   * Point `store.getters.hosting` at a given service.
   *
   * The shared mailbox modals (ChangePassword, DeleteEmail) dispatch
   * `email/*` actions that fall back to `store.getters.hosting.id` when no
   * service is passed, so without this they act on whichever service was opened
   * last. This is a deliberate global mutation — the same state a visit to that
   * service's hosting page would leave behind.
   */
  async ensureHostingContext ({ dispatch, rootGetters }, serviceId) {
    const service = await dispatch('resolveService', serviceId)
    if (!service) return false

    const { hosting } = rootGetters
    if (hosting && hosting.id === service.id) return true

    try {
      await dispatch('getHosting', service.id, { root: true })
      return true
    } catch (e) {
      // Reported by the caller, which knows what the customer was trying to do.
      console.warn(`emailHub: unable to open hosting context for service ${serviceId}`, e)
      return false
    }
  },

  /**
   * The forwarders that deliver to this mailbox.
   *
   * `list_forwarders` names its fields the opposite way round to what you would
   * guess — see aliasCountsByTarget above — so a mailbox's aliases are the rows
   * whose `forward` is the mailbox, and each row's own address is `dest`.
   */
  async loadAliases ({ dispatch }, { serviceId, email }) {
    const service = await dispatch('resolveService', serviceId)
    if (!service) return []

    const response = await amapi.cpanel_uapi({
      id: service.id,
      _username: service.username,
      uapi_module: 'Email',
      uapi_function: 'list_forwarders',
    })

    const address = String(email || '').toLowerCase()
    return (response?.data || [])
      .filter(row => String(row?.forward || '').toLowerCase() === address)
  },

  /**
   * Point a new address at an existing mailbox.
   *
   * `add_forwarder` names its parameters from the forwarder's point of view:
   * `email` is the alias's own address and `fwdemail` is where the mail is
   * delivered — the same orientation `list_forwarders` reports back as `dest`
   * and `forward`. `fwdopt: 'fwd'` is the only mode this screen offers; cPanel
   * also supports discard, pipe and system, which are out of scope.
   *
   * Aliases point only at mailboxes on the account (AM-1104, Kaumil
   * 2026-09-23), which the form enforces by never letting the customer type a
   * destination — it is always the mailbox they opened.
   */
  async addAlias ({ dispatch }, { serviceId, dest, domain, forward }) {
    const service = await dispatch('resolveService', serviceId)
    if (!service) throw new Error('UNK_SRV')

    return amapi.cpanel_uapi({
      id: service.id,
      _username: service.username,
      uapi_module: 'Email',
      uapi_function: 'add_forwarder',
      domain,
      email: dest,
      fwdopt: 'fwd',
      fwdemail: forward,
    })
  },

  /**
   * `delete_forwarder` takes the alias's own address as `address` and its
   * destination as `forwarder` — the same mapping EmailForwardersTable uses.
   */
  async deleteAlias ({ dispatch }, { serviceId, dest, forward }) {
    const service = await dispatch('resolveService', serviceId)
    if (!service) throw new Error('UNK_SRV')

    return amapi.cpanel_uapi({
      id: service.id,
      _username: service.username,
      uapi_module: 'Email',
      uapi_function: 'delete_forwarder',
      address: dest,
      forwarder: forward,
    })
  },

  /**
   * Forward mail arriving at a mailbox on to another address.
   *
   * The same `add_forwarder` call as `addAlias`, the other way round: here the
   * mailbox is the forwarder's own address (`email`) and the customer types
   * the destination. cPanel still delivers a copy to the mailbox itself.
   */
  async addMailboxForwarder ({ dispatch }, { serviceId, email, domain, destination }) {
    const service = await dispatch('resolveService', serviceId)
    if (!service) throw new Error('UNK_SRV')

    return amapi.cpanel_uapi({
      id: service.id,
      _username: service.username,
      uapi_module: 'Email',
      uapi_function: 'add_forwarder',
      domain,
      email,
      fwdopt: 'fwd',
      fwdemail: destination,
    })
  },

  /**
   * The mailbox's automatic reply, or null when it has none.
   *
   * `get_auto_responder` answers an address without one with an empty record
   * rather than an error, so an empty subject and body read as "none".
   */
  async loadAutoResponder ({ dispatch }, { serviceId, email }) {
    const service = await dispatch('resolveService', serviceId)
    if (!service) return null

    const response = await amapi.cpanel_uapi({
      id: service.id,
      _username: service.username,
      uapi_module: 'Email',
      uapi_function: 'get_auto_responder',
      email,
    })
    const data = response?.data
    if (!data || (!data.subject && !data.body)) return null
    return data
  },

  /**
   * Create or replace the mailbox's automatic reply — `add_auto_responder`
   * overwrites an existing one for the same address. `email` is the local part
   * only; `start`/`stop` are Unix times, empty for "now" and "never".
   */
  async saveAutoResponder ({ dispatch }, { serviceId, user, domain, from, subject, body, interval, isHtml, start, stop }) {
    const service = await dispatch('resolveService', serviceId)
    if (!service) throw new Error('UNK_SRV')

    return amapi.cpanel_uapi({
      id: service.id,
      _username: service.username,
      uapi_module: 'Email',
      uapi_function: 'add_auto_responder',
      email: user,
      domain,
      from,
      subject,
      body,
      interval,
      is_html: isHtml ? 1 : 0,
      charset: 'utf-8',
      start: start || '',
      stop: stop || '',
    })
  },

  /**
   * Where a domain's unrouted mail currently goes (cPanel's "default address").
   */
  async loadDefaultAddress ({ dispatch }, { serviceId, domain }) {
    const service = await dispatch('resolveService', serviceId)
    if (!service) return null

    const response = await amapi.cpanel_uapi({
      id: service.id,
      _username: service.username,
      uapi_module: 'Email',
      uapi_function: 'list_default_address',
      domain,
    })
    const row = (response?.data || [])[0]
    return row ? row.defaultaddress : null
  },

  /**
   * Make a mailbox the catch-all for its domain: every message to an address
   * on the domain that has no mailbox or forwarder lands in it.
   */
  async setCatchAll ({ dispatch }, { serviceId, domain, email }) {
    const service = await dispatch('resolveService', serviceId)
    if (!service) throw new Error('UNK_SRV')

    return amapi.cpanel_uapi({
      id: service.id,
      _username: service.username,
      uapi_module: 'Email',
      uapi_function: 'set_default_address',
      domain,
      fwdopt: 'fwd',
      fwdemail: email,
    })
  },

  /**
   * Whether the account has any forwarder at all, aliases included — the
   * Forwarders tab lists both. Used for the tab's empty state (AM-1107).
   */
  async hasForwarders ({ dispatch }, serviceId) {
    const service = await dispatch('resolveService', serviceId)
    if (!service) return false

    const response = await amapi.cpanel_uapi({
      id: service.id,
      _username: service.username,
      uapi_module: 'Email',
      uapi_function: 'list_forwarders',
    })
    return (response?.data || []).length > 0
  },

  /**
   * Whether any domain on the account has an autoresponder.
   *
   * `list_auto_responders` is read per domain, the same way the Autoresponders
   * tab reads it, so this walks the account's domains and stops at the first
   * hit. A failure on one domain fails the whole check: the caller then shows
   * the full tab rather than an empty state that may be wrong.
   */
  async hasAutoResponders ({ dispatch, getters }, serviceId) {
    const service = await dispatch('resolveService', serviceId)
    if (!service) return false

    await dispatch('loadDomainsForService', service.id)
    const domains = getters.mailboxDomainsForService(service.id)

    for (const domain of domains) {
      const response = await amapi.cpanel_uapi({
        id: service.id,
        _username: service.username,
        uapi_module: 'Email',
        uapi_function: 'list_auto_responders',
        domain,
      })
      if ((response?.data || []).length) return true
    }
    return false
  },

  /**
   * Whether any address on the account — every mailbox and the cPanel user
   * itself, as the Filters tab lists them — has a filter. Read in small
   * batches and stopped at the first hit; a failure fails the check, as in
   * hasAutoResponders.
   */
  async hasFilters ({ dispatch }, serviceId) {
    const service = await dispatch('resolveService', serviceId)
    if (!service) return false

    const base = {
      id: service.id,
      _username: service.username,
      uapi_module: 'Email',
    }
    const pops = await amapi.cpanel_uapi({ ...base, uapi_function: 'list_pops' })
    const accounts = (pops?.data || []).map(row => row.email).filter(Boolean)

    for (let i = 0; i < accounts.length; i += FANOUT_CONCURRENCY) {
      const batch = accounts.slice(i, i + FANOUT_CONCURRENCY)
      const responses = await Promise.all(batch.map(account => {
        return amapi.cpanel_uapi({ ...base, uapi_function: 'list_filters', account })
      }))
      if (responses.some(response => (response?.data || []).length)) return true
    }
    return false
  },

  async loadClientSettings ({ dispatch }, { serviceId, account }) {
    const service = await dispatch('resolveService', serviceId)
    if (!service) return null

    const response = await amapi.cpanel_uapi({
      id: service.id,
      _username: service.username,
      uapi_module: 'Email',
      uapi_function: 'get_client_settings',
      account,
    })
    return response?.data || null
  },

  /**
   * `to` is optional: omitted, cPanel sends the settings to the mailbox itself,
   * which is what the welcome mail on a newly created mailbox wants.
   */
  async sendClientSettings ({ dispatch }, { serviceId, account, to }) {
    const service = await dispatch('resolveService', serviceId)
    if (!service) throw new Error('UNK_SRV')

    return amapi.cpanel_uapi({
      id: service.id,
      _username: service.username,
      uapi_module: 'Email',
      uapi_function: 'dispatch_client_settings',
      account,
      ...(to ? { to } : {}),
    })
  },

  /**
   * Change one restriction of a mailbox — `kind` is incoming, outgoing or login.
   *
   * cPanel has no single "set" call, only a pair per restriction, so the
   * current one is lifted before the new one is applied. That matters for
   * outgoing mail, where Suspend and Hold are separate flags: switching
   * straight from one to the other would otherwise leave both set.
   */
  async setMailboxRestriction ({ dispatch }, { serviceId, email, kind, from, to }) {
    const service = await dispatch('resolveService', serviceId)
    if (!service) throw new Error('UNK_SRV')

    const calls = RESTRICTION_CALLS[kind] || {}
    const steps = []
    if (calls[from]) steps.push(calls[from][1])
    if (calls[to]) steps.push(calls[to][0])

    for (const uapiFunction of steps) {
      await amapi.cpanel_uapi({
        id: service.id,
        _username: service.username,
        uapi_module: 'Email',
        uapi_function: uapiFunction,
        email,
      })
    }
  },

  /**
   * Whether mail to a plus address (`user+folder@domain`) files itself into a
   * folder created on the fly. Not on the `list_pops_with_disk` row, so read on
   * its own.
   */
  async loadMailboxAutocreate ({ dispatch }, { serviceId, email }) {
    const service = await dispatch('resolveService', serviceId)
    if (!service) return null

    const response = await amapi.cpanel_uapi({
      id: service.id,
      _username: service.username,
      uapi_module: 'Email',
      uapi_function: 'get_mailbox_autocreate',
      email,
    })
    // cPanel returns the flag as `data` itself on some builds and as
    // `data.autocreate` on others; `Number()` copes with both 1/0 and booleans.
    const data = response?.data
    const value = data !== null && typeof data === 'object' ? data.autocreate : data
    return Number(value) === 1
  },

  async setMailboxAutocreate ({ dispatch }, { serviceId, email, enabled }) {
    const service = await dispatch('resolveService', serviceId)
    if (!service) throw new Error('UNK_SRV')

    return amapi.cpanel_uapi({
      id: service.id,
      _username: service.username,
      uapi_module: 'Email',
      uapi_function: enabled ? 'enable_mailbox_autocreate' : 'disable_mailbox_autocreate',
      email,
    })
  },

  clearMailboxes ({ commit }) {
    commit('CLEAR_MAILBOXES')
  },
}

export default {
  namespaced: true,
  state,
  getters,
  mutations,
  actions
}
